import React, { useMemo, useState, useEffect, useRef } from 'react';
import MultiBreedSelect from '../MultiBreedSelect';
import './DogCard.css';

const TEMPERAMENT_OPTIONS = [
  'Gets along with other dogs',
  'Good with small dogs',
  'Leash reactive',
  'High energy',
  'Anxious / nervous',
  'House trained',
  'Crate trained',
  'Has been to daycare/boarding before',
];

// Placeholder template shown in care mode — mirrors the format the staff
// briefing parses well (feeding / meds / potty / vet / emergency contact).
const CARE_PLACEHOLDER = `Food: 1 cup Nulo per day — 1/2 cup morning, 1/2 cup before bed
Meds: none
Potty: 3–4 walks a day
Vet: Sequoia Vet Hospital, 650-369-7326
Emergency contact: Jane Doe, 555-123-4567`;

const DogCard = ({ index, dog, onDogChange, onRemove, removable, hidePhoto = false, slim = false, careMode = false }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [photoError, setPhotoError] = useState('');
  const blobUrlRef = useRef(null);

  // Manage blob URL lifecycle to prevent memory leaks.
  // Revokes old URL whenever photoFile changes and on unmount.
  useEffect(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    if (dog.photoFile) {
      const url = URL.createObjectURL(dog.photoFile);
      blobUrlRef.current = url;
      setPreviewUrl(url);
    } else if (dog.defaultImageUrl) {
      setPreviewUrl(dog.defaultImageUrl);
    } else {
      setPreviewUrl(null);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [dog.photoFile, dog.defaultImageUrl]);

  const parseDogBreeds = (breeds) => {
    if (!breeds) return [];
    if (Array.isArray(breeds) && typeof breeds[0] === 'object' && breeds[0] && breeds[0].value) return breeds;
    return Array.isArray(breeds) ? breeds.map((breed) => ({ label: breed, value: breed })) : [];
  };

  const parsedBreeds = useMemo(() => parseDogBreeds(dog.breeds), [dog.breeds]);

  const handleChange = (field, value) => {
    onDogChange(index, field, value);
    // Preview URL is managed by the useEffect watching dog.photoFile
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPhotoError('');

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setPhotoError('Please upload a JPEG or PNG image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB.');
      return;
    }

    handleChange('photoFile', file);
  };

  const toggleTemperament = (trait) => {
    const current = dog.temperament || [];
    const updated = current.includes(trait)
      ? current.filter((t) => t !== trait)
      : [...current, trait];
    handleChange('temperament', updated);
  };

  if (slim) {
    return (
      <div className="dog-card-inline dog-card-slim">
        <h3 className="dog-card-header-inline">Dog #{index + 1}</h3>

        <div className="dog-form-section">
          <div>
            <label className="dog-field-label" htmlFor={`dogName-${index}`}>Dog Name</label>
            <input
              id={`dogName-${index}`}
              type="text"
              placeholder="Enter dog's name"
              className="dog-card-input-inline"
              value={dog.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </div>

          <div>
            <label className="dog-field-label" htmlFor={`dogBreeds-${index}`}>Breed(s)</label>
            <MultiBreedSelect
              inputId={`dogBreeds-${index}`}
              value={parsedBreeds}
              onChange={(selected) => handleChange('breeds', selected)}
              placeholder="Select one or more breeds"
            />
          </div>

          <div className="dog-age-field">
            <div className="age-input-group">
              <label htmlFor={`dogAgeYears-${index}`}>Years</label>
              <input
                id={`dogAgeYears-${index}`}
                type="number"
                placeholder="0"
                value={dog.years ?? ''}
                onChange={(e) => handleChange('years', e.target.value)}
                min="0"
                max="30"
                inputMode="numeric"
              />
            </div>
            <div className="age-input-group">
              <label htmlFor={`dogAgeMonths-${index}`}>Months</label>
              <input
                id={`dogAgeMonths-${index}`}
                type="number"
                placeholder="0"
                value={dog.months ?? ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  handleChange('months', isNaN(val) ? '' : Math.min(Math.max(val, 0), 11));
                }}
                min="0"
                max="11"
                inputMode="numeric"
              />
            </div>
          </div>
        </div>

        {removable && (
          <div className="dog-card-remove-container-inline">
            <button
              type="button"
              className="dog-card-remove-button-inline"
              onClick={onRemove}
              title="Remove this dog"
            >
              Remove Dog
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="dog-card-inline">
      <h3 className="dog-card-header-inline">Dog #{index + 1}</h3>

      <div className="dog-form-section">
        <div>
          <label className="dog-field-label" htmlFor={`dogName-${index}`}>Dog Name</label>
          <input
            id={`dogName-${index}`}
            type="text"
            placeholder="Enter dog's name"
            className="dog-card-input-inline"
            value={dog.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            required
          />
        </div>

        {!hidePhoto && (
          <div className="dog-photo-upload">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Dog preview"
                className="dog-photo-preview"
                onError={() => {
                  // Blob URL failed — retry with FileReader as fallback
                  if (dog.photoFile && previewUrl?.startsWith('blob:')) {
                    const reader = new FileReader();
                    reader.onload = (ev) => setPreviewUrl(ev.target.result);
                    reader.readAsDataURL(dog.photoFile);
                  }
                }}
              />
            ) : (
              <div className="dog-photo-placeholder">
                📷<br />No photo
              </div>
            )}
            <div className="dog-photo-buttons">
              <label>
                📷 Upload Photo
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
              </label>
            </div>
            {photoError && <p role="alert" className="dog-photo-error">{photoError}</p>}
          </div>
        )}

        <div>
          <label className="dog-field-label" htmlFor={`dogBreedsFull-${index}`}>Breed(s)</label>
          <MultiBreedSelect
            inputId={`dogBreedsFull-${index}`}
            value={parsedBreeds}
            onChange={(selected) => handleChange('breeds', selected)}
            placeholder="Select one or more breeds"
          />
        </div>

        <div>
          <label className="dog-field-label" htmlFor={`dogGender-${index}`}>Gender</label>
          <select
            id={`dogGender-${index}`}
            className="dog-card-input-inline gender"
            value={dog.gender}
            onChange={(e) => handleChange('gender', e.target.value)}
            required
          >
            <option value="" disabled>Select gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>

        <div className="dog-age-field">
          <div className="age-input-group">
            <label htmlFor={`dogAgeYears-${index}`}>Years</label>
            <input
              id={`dogAgeYears-${index}`}
              type="number"
              placeholder="0"
              value={dog.years ?? ''}
              onChange={(e) => handleChange('years', e.target.value)}
              min="0"
              max="30"
              inputMode="numeric"
              required
            />
          </div>
          <div className="age-input-group">
            <label htmlFor={`dogAgeMonths-${index}`}>Months</label>
            <input
              id={`dogAgeMonths-${index}`}
              type="number"
              placeholder="0"
              value={dog.months ?? ''}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                handleChange('months', isNaN(val) ? '' : Math.min(Math.max(val, 0), 11));
              }}
              min="0"
              max="11"
              inputMode="numeric"
              required
            />
          </div>
          <div className="age-input-group">
            <label htmlFor={`dogWeight-${index}`}>Weight (lbs)</label>
            <input
              id={`dogWeight-${index}`}
              type="number"
              placeholder="0"
              value={dog.weight || ''}
              onChange={(e) => handleChange('weight', e.target.value)}
              min="1"
              max="300"
              required
            />
          </div>
        </div>

        <div>
          <label className="dog-field-label" htmlFor={`dogNotes-${index}`}>
            {careMode ? 'Care & feeding instructions' : 'Additional Notes'}
          </label>
          {careMode && (
            <p className="dog-care-help">
              Shown to staff on the daily dashboard. Include feeding, meds, potty
              routine, and vet / emergency contacts — plain sentences are fine.
            </p>
          )}
          <textarea
            id={`dogNotes-${index}`}
            className="dog-note-input-inline"
            placeholder={careMode ? CARE_PLACEHOLDER : "Any additional information about the dog (behavior, medical conditions, preferences, etc.)"}
            value={dog.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={careMode ? 8 : undefined}
          />
        </div>

        {/* Temperament toggles */}
        <div className="dog-temperament-section">
          <label className="dog-field-label">Temperament</label>
          <div className="dog-temperament-pills">
            {TEMPERAMENT_OPTIONS.map((trait) => (
              <button
                key={trait}
                type="button"
                className={`dog-temperament-pill${(dog.temperament || []).includes(trait) ? ' active' : ''}`}
                onClick={() => toggleTemperament(trait)}
              >
                {trait}
              </button>
            ))}
          </div>
        </div>
      </div>

      {removable && (
        <div className="dog-card-remove-container-inline">
          <button
            type="button"
            className="dog-card-remove-button-inline"
            onClick={onRemove}
            title="Remove this dog"
          >
            Remove Dog
          </button>
        </div>
      )}
    </div>
  );
};

export default DogCard;
